/**
 * `EmailMessageList` — envelopes for one mailbox, newest first (server order),
 * read-only. Each row opens the full message. See the C3 handoff.
 */
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2, Paperclip } from "lucide-react";

import { useMailList } from "@/hooks/useMail";
import { MailListSkeleton, MailWaitingCue } from "@/components/agent/MailWaitingCue";
import type { MailEnvelope } from "@/api/lx-mail";
import { decodeMimeWords } from "@/lib/mime-decode";
import { mailboxShowsRecipient, participantLabel } from "@/lib/mail-address";
import { useContactNames } from "@/hooks/useContactNames";

export function EmailMessageList({
  mailbox,
  mailboxName,
  onSelect,
  selectedId = null,
  autoSelectFirst = false,
}: {
  mailbox: string;
  /** Localized folder label — lets a sent-like folder be recognised even when
   *  its id is opaque. */
  mailboxName?: string;
  onSelect: (envelope: MailEnvelope) => void;
  /** Highlights the open message when a persistent viewer column is shown. */
  selectedId?: string | null;
  /**
   * Open the newest message as soon as the mailbox loads, so the reading pane is
   * never an empty box next to a full list. Only passed when the viewer is
   * actually VISIBLE — in the narrow layout it would swap the list away before
   * the lawyer had a chance to look at it.
   */
  autoSelectFirst?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { messages, skipped, total, hasMore, isLoading, isLoadingMore, error, loadOlder } = useMailList(
    mailbox,
    { enabled: true },
  );
  const fmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: "short", timeStyle: "short" }),
    [i18n.language],
  );
  const { resolve: resolveContactName } = useContactNames();

  // WHICH participant identifies a row depends on the folder. In a received
  // folder the sender is the correspondent; in a SENT folder the sender is
  // always the agent itself, so every row would read identically and tell the
  // lawyer nothing — there, the RECIPIENT is the message's identity. Outlook
  // draws the same distinction, and the operator called the old uniform
  // "from" column out as inconsistent.
  const showRecipient = useMemo(
    () => mailboxShowsRecipient(mailbox, mailboxName),
    [mailbox, mailboxName],
  );

  // Newest-first, so messages[0] is the most recent.
  const first = messages[0];
  useEffect(() => {
    if (!autoSelectFirst || selectedId || !first) return;
    onSelect(first);
  }, [autoSelectFirst, selectedId, first, onSelect]);

  if (isLoading && messages.length === 0) {
    // Skeleton + an escalating cue rather than a bare spinner: a cold folder
    // read can take 30 s+ (one fresh IMAP session per himalaya call), and at
    // that length a spinner reads as a hang. See MailWaitingCue.
    return (
      <div data-testid="email-messages-loading" className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center border-b border-cream-200/60 px-3 py-2 dark:border-coffee-800/60">
          <MailWaitingCue />
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          <MailListSkeleton />
        </div>
      </div>
    );
  }

  if (error && messages.length === 0) {
    return (
      <div
        data-testid="email-list-error"
        role="alert"
        className="m-3 rounded border border-red-200 bg-red-50 p-3 text-body-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
      >
        {t("agent.channels.loadFailed", { message: error })}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div
        data-testid="email-messages-empty"
        className="py-16 text-center text-body-sm text-cream-500"
      >
        {t("agent.channels.email.noMessages")}
      </div>
    );
  }

  return (
    <div data-testid="email-messages" className="flex min-h-0 flex-1 flex-col">
      {/* A short page must never masquerade as a small folder. The runtime
          isolates an unreadable message rather than failing the page
          (`skipped`, added in 8e2c1d89) — so the pane says so out loud. */}
      {skipped > 0 && (
        <div
          data-testid="email-skipped-notice"
          role="status"
          className="flex shrink-0 items-center gap-1.5 border-b border-amber-300/50 bg-amber-50/70 px-3 py-1 text-[11px] text-amber-800 dark:border-amber-800/40 dark:bg-amber-950/30 dark:text-amber-300"
        >
          <AlertTriangle size={11} className="shrink-0" aria-hidden />
          {t("agent.channels.email.skippedNotice", { count: skipped })}
        </div>
      )}
      <ul className="min-h-0 flex-1 divide-y divide-cream-200/60 overflow-y-auto dark:divide-coffee-800/60">
        {messages.map((m) => {
          const who = participantLabel(showRecipient ? m.to : m.from, resolveContactName);
          return (
          <li key={m.messageId}>
            <button
              type="button"
              data-testid="email-message-row"
              data-message-id={m.messageId}
              aria-current={m.messageId === selectedId ? "true" : undefined}
              onClick={() => onSelect(m)}
              className={
                "flex w-full flex-col px-3 py-1.5 text-left leading-tight transition-colors " +
                (m.messageId === selectedId
                  ? "bg-cream-200/60 dark:bg-coffee-800/70"
                  : "hover:bg-cream-100/60 dark:hover:bg-coffee-900/50")
              }
            >
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className={
                    // Unread is signalled by WEIGHT ALONE — the fill stays grey.
                    // Colour-bumping unread makes a mailbox where nothing has
                    // been opened (the common case for an agent's inbox) render
                    // as a wall of bright text, drowning the subject, which is
                    // what the eye actually scans.
                    "min-w-0 flex-1 truncate text-caption text-coffee-600 dark:text-cream-400" +
                    (m.unread ? " font-medium" : "")
                  }
                  title={showRecipient ? m.to : m.from}
                >
                  {decodeMimeWords(who.label) ||
                    t(showRecipient ? "agent.channels.unknownRecipient" : "agent.channels.unknownSender")}
                  {who.extraCount > 0 && (
                    <span className="ml-1 text-cream-500">+{who.extraCount}</span>
                  )}
                </span>
                <span className="flex shrink-0 items-center gap-1 text-[11px] text-cream-500">
                  {m.hasAttachments && <Paperclip size={10} aria-hidden />}
                  {fmt.format(m.date)}
                </span>
              </div>
              {/* The SUBJECT leads the row — one step larger and lighter than
                  the grey sender above it, because it is what the eye scans. */}
              <span className="truncate text-body-sm text-coffee-700 dark:text-cream-300">
                {decodeMimeWords(m.subject) || t("agent.channels.email.noSubject")}
              </span>
              {/* The runtime fills `snippet` only in mail.get, not in the list —
                  hide an empty preview line rather than render a blank row. */}
              {m.snippet && (
                <span className="line-clamp-1 text-[11px] text-coffee-500 dark:text-cream-500">
                  {m.snippet}
                </span>
              )}
            </button>
          </li>
          );
        })}
      </ul>
      {(hasMore || total > 0) && (
        <div className="flex shrink-0 items-center justify-center gap-2 border-t border-cream-200/60 py-1.5 text-center dark:border-coffee-800/60">
          {total > 0 && (
            <span className="text-[11px] text-cream-500">
              {t("agent.channels.email.countOfTotal", {
                shown: messages.length,
                total,
              })}
            </span>
          )}
          {hasMore && (
          <button
            type="button"
            data-testid="email-load-older"
            onClick={() => void loadOlder()}
            disabled={isLoadingMore}
            className="inline-flex items-center gap-1.5 rounded-md border border-cream-300 px-3 py-1 text-body-sm text-coffee-600 transition-colors hover:bg-cream-100 disabled:opacity-50 dark:border-coffee-700 dark:text-cream-300 dark:hover:bg-coffee-800"
          >
            {isLoadingMore && <Loader2 size={12} className="animate-spin" />}
            {t("agent.channels.email.loadOlder")}
          </button>
          )}
        </div>
      )}
    </div>
  );
}
